import React from 'react';
import type { WindRoseSector } from '../../../domain/WeatherStation';
import { compassSectorLabel } from '../../../domain/WeatherStation';

interface Props {
  sectors: WindRoseSector[];
  size?: number;
}

// Rosa de los vientos: un pétalo por rumbo (16), largo según qué porcentaje
// del tiempo sopló desde ahí, y más opaco cuanto más fuerte sopló en promedio.
const WindRose: React.FC<Props> = ({ sectors, size = 240 }) => {
  const total = sectors.reduce((acc, s) => acc + s.count, 0);
  if (total === 0) {
    return <p className="station-empty">Todavía no hay datos de dirección del viento en este período.</p>;
  }

  const c = 120;
  const maxR = 92;
  const maxShare = Math.max(...sectors.map((s) => s.count / total));
  const maxSpeed = Math.max(1, ...sectors.map((s) => s.avgSpeed ?? 0));
  const half = (22.5 / 2) * 0.82 * (Math.PI / 180);

  const petal = (sector: WindRoseSector) => {
    const share = sector.count / total;
    if (share === 0) return null;
    const r = Math.max(6, (share / maxShare) * maxR);
    const mid = sector.sector * 22.5 * (Math.PI / 180);
    const a1 = mid - half;
    const a2 = mid + half;
    const p = (a: number, radius: number) => `${c + Math.sin(a) * radius},${c - Math.cos(a) * radius}`;
    const d = `M ${c},${c} L ${p(a1, r)} A ${r} ${r} 0 0 1 ${p(a2, r)} Z`;
    const opacity = 0.35 + 0.65 * ((sector.avgSpeed ?? 0) / maxSpeed);
    return (
      <path key={sector.sector} d={d} className="wind-rose-petal" fillOpacity={opacity}>
        <title>
          {compassSectorLabel(sector.sector)}: {Math.round(share * 100)}% del tiempo
          {sector.avgSpeed !== null ? `, promedio ${Math.round(sector.avgSpeed)} km/h` : ''}
        </title>
      </path>
    );
  };

  return (
    <svg viewBox="0 0 240 240" width="100%" style={{ maxWidth: size }} className="wind-rose" role="img" aria-label="Rosa de los vientos">
      {[0.25, 0.5, 0.75, 1].map((f) => (
        <circle key={f} cx={c} cy={c} r={maxR * f} className="wind-rose-ring" />
      ))}
      {[0, 45, 90, 135].map((deg) => {
        const rad = (deg * Math.PI) / 180;
        return (
          <line
            key={deg}
            x1={c - Math.sin(rad) * maxR}
            y1={c + Math.cos(rad) * maxR}
            x2={c + Math.sin(rad) * maxR}
            y2={c - Math.cos(rad) * maxR}
            className="wind-rose-ring"
          />
        );
      })}
      {sectors.map(petal)}
      {[
        ['N', 0],
        ['E', 90],
        ['S', 180],
        ['O', 270],
      ].map(([label, deg]) => {
        const rad = ((deg as number) * Math.PI) / 180;
        return (
          <text
            key={label}
            x={c + Math.sin(rad) * 108}
            y={c - Math.cos(rad) * 108 + 4}
            textAnchor="middle"
            className="wind-rose-label"
          >
            {label}
          </text>
        );
      })}
      <text x={c + 4} y={c - maxR + 11} className="wind-rose-scale">
        {Math.round(maxShare * 100)}%
      </text>
    </svg>
  );
};

export default WindRose;
